"use client";

import { useCallback, useId, useMemo, useState } from "react";
import type {
  SourceField,
  SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { CitationForm } from "@/components/tools/special-sources/citation-form";
import { LookupRow } from "@/components/tools/special-sources/lookup-row";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ClinicalTrialLabels,
  clinicalTrialIsReady,
  CLINICAL_TRIAL_FIELDS as F,
  formatClinicalTrial,
  normalizeNct,
  valuesFromCtGov,
} from "@/lib/tool-kit/special-sources/clinical-trial-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const CLINICAL_TRIAL_CITATION_GENERATOR_STRINGS = {
  formHeading: "Trial registration details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the trial's title and its registration number to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  lookupLabel: "Fill the form from ClinicalTrials.gov (optional)",
  lookupPlaceholder: "NCT04381936",
  lookupButton: "Look up",
  lookupLoading: "Looking up…",
  lookupHint:
    "Enter an NCT number. The tool asks ClinicalTrials.gov's public API for that record — only the number is sent — and fills in the form for you to check.",
  lookupInvalid:
    "That is not an NCT number. It is the letters NCT followed by eight digits, such as NCT04381936.",
  lookupNotFound:
    "ClinicalTrials.gov has no record {id}. Check the number, or fill in the form by hand.",
  lookupFailed:
    "ClinicalTrials.gov could not be reached just now. Try again in a moment, or fill in the form by hand.",
  lookupDone:
    "Filled in from record {id}. Check the title: ClinicalTrials.gov capitalizes every word, while APA, AMA and NLM all want sentence case.",
  registryLabel: "Registry",
  registryClinicalTrials: "ClinicalTrials.gov (NCT number)",
  registryIsrctn: "ISRCTN registry (ISRCTN number)",
  registryEuctr: "EU Clinical Trials Register (EudraCT number)",
  registryCtis: "CTIS — Clinical Trials in the European Union (EU CT number)",
  registryAnzctr: "ANZCTR (ACTRN number)",
  registryOther: "Another registry",
  registryNameLabel: "Registry name",
  registryNamePlaceholder: "Clinical Trials Registry – India",
  registryPublisherLabel: "Registry's place and publisher (for Vancouver)",
  registryPublisherPlaceholder: "New Delhi: Indian Council of Medical Research",
  identifierLabel: "Registration number",
  identifierPlaceholder: "NCT04381936",
  titleLabel: "Trial title",
  titlePlaceholder: "Randomised evaluation of COVID-19 therapy (RECOVERY)",
  titleHint:
    "The record's brief title in sentence case, with any acronym in parentheses at the end.",
  authorTypeLabel: "The responsible party is",
  authorTypeOrganization: "An organization (the sponsor)",
  authorTypePerson: "A person (an investigator)",
  authorLabel: "Responsible party or sponsor",
  authorPlaceholder: "University of Oxford",
  authorHint:
    "As the record names it. For a person, type the first name then the surname.",
  firstPostedLabel: "First posted",
  lastUpdatedLabel: "Last update posted",
  accessedLabel: "Date you viewed the record",
  datePlaceholder: "YYYY-MM-DD",
  urlLabel: "Record URL",
  urlPlaceholder: "https://clinicaltrials.gov/study/NCT04381936",
  urlHint:
    "Optional for ClinicalTrials.gov and ISRCTN records — the tool uses the record's permanent address.",
  apaLabel: "APA 7th edition",
  amaLabel: "AMA 11th edition",
  vancouverLabel: "Vancouver (NLM)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  apaNote:
    "APA has no clinical-trial example of its own. This follows its webpage format, dated by the last update, with the registry number in parentheses after the title — the way APA places ERIC and report numbers.",
  amaNote:
    "In the text, cite the trial by its superscript reference number. AMA's own example starts with the trial's title and names no author.",
  vancouverNote:
    "Follows the ClinicalTrials.gov example in NLM's Citing Medicine. In the text, cite the trial by its reference number.",
  example: {
    registry: "clinicaltrials",
    identifier: "NCT04381936",
    title: "Randomised evaluation of COVID-19 therapy (RECOVERY)",
    authorType: "organization",
    author: "University of Oxford",
    firstPosted: "2020-05-11",
    lastUpdated: "2026-04-21",
    accessed: "2026-10-05",
    url: "https://clinicaltrials.gov/study/NCT04381936",
  },
} as const;

type Strings = typeof CLINICAL_TRIAL_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings, other: boolean): SourceField[] {
  const fields: SourceField[] = [
    {
      id: F.registry,
      kind: "select",
      label: strings.registryLabel,
      options: [
        { value: "clinicaltrials", label: strings.registryClinicalTrials },
        { value: "isrctn", label: strings.registryIsrctn },
        { value: "euctr", label: strings.registryEuctr },
        { value: "ctis", label: strings.registryCtis },
        { value: "anzctr", label: strings.registryAnzctr },
        { value: "other", label: strings.registryOther },
      ],
    },
    {
      id: F.identifier,
      kind: "text",
      label: strings.identifierLabel,
      placeholder: strings.identifierPlaceholder,
    },
  ];
  if (other) {
    fields.push(
      {
        id: F.registryName,
        kind: "text",
        label: strings.registryNameLabel,
        placeholder: strings.registryNamePlaceholder,
      },
      {
        id: F.registryPublisher,
        kind: "text",
        label: strings.registryPublisherLabel,
        placeholder: strings.registryPublisherPlaceholder,
      }
    );
  }
  fields.push(
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.authorType,
      kind: "select",
      label: strings.authorTypeLabel,
      options: [
        { value: "organization", label: strings.authorTypeOrganization },
        { value: "person", label: strings.authorTypePerson },
      ],
    },
    {
      id: F.author,
      kind: "text",
      label: strings.authorLabel,
      placeholder: strings.authorPlaceholder,
      hint: strings.authorHint,
    },
    {
      id: F.firstPosted,
      kind: "date",
      label: strings.firstPostedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.lastUpdated,
      kind: "date",
      label: strings.lastUpdatedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
    }
  );
  return fields;
}

/** The widget's strings, as the formatter's labels. */
export function clinicalTrialLabels(strings: Strings): ClinicalTrialLabels {
  return {
    apa: strings.apaLabel,
    ama: strings.amaLabel,
    vancouver: strings.vancouverLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    apaNote: strings.apaNote,
    amaNote: strings.amaNote,
    vancouverNote: strings.vancouverNote,
  };
}

/** Today in the reader's own time zone, as YYYY-MM-DD. Call from a handler only. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

type LookupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; message: string }
  | { status: "error"; message: string };

export default function ClinicalTrialCitationGeneratorWidget() {
  const strings = useToolUi(CLINICAL_TRIAL_CITATION_GENERATOR_STRINGS);
  const lookupId = useId();
  const [values, setValues] = useState<SourceValues>({});
  const [nct, setNct] = useState("");
  const [lookup, setLookup] = useState<LookupState>({ status: "idle" });
  const other = values[F.registry] === "other";
  const fields = useMemo(() => buildFields(strings, other), [strings, other]);
  const labels = useMemo(() => clinicalTrialLabels(strings), [strings]);
  const outputs = useMemo(
    () =>
      clinicalTrialIsReady(values) ? formatClinicalTrial(values, labels) : [],
    [values, labels]
  );

  const update = useCallback((id: string, value: string) => {
    setValues((current) => ({ ...current, [id]: value }));
  }, []);

  async function runLookup() {
    const id = normalizeNct(nct);
    if (!id) {
      setLookup({ status: "error", message: strings.lookupInvalid });
      return;
    }
    setLookup({ status: "loading" });
    try {
      const response = await fetch(
        `https://clinicaltrials.gov/api/v2/studies/${id}`
      );
      if (response.status === 404) {
        setLookup({
          status: "error",
          message: t(strings.lookupNotFound, { id }),
        });
        return;
      }
      if (!response.ok) {
        throw new Error(String(response.status));
      }
      const filled = valuesFromCtGov(await response.json(), today());
      if (!filled) {
        setLookup({
          status: "error",
          message: t(strings.lookupNotFound, { id }),
        });
        return;
      }
      setValues(filled);
      setLookup({ status: "done", message: t(strings.lookupDone, { id }) });
    } catch {
      setLookup({ status: "error", message: strings.lookupFailed });
    }
  }

  return (
    <CitationForm
      fields={fields}
      intro={
        <LookupRow
          buttonLabel={strings.lookupButton}
          error={lookup.status === "error" ? lookup.message : ""}
          hint={strings.lookupHint}
          id={`${lookupId}-nct`}
          label={strings.lookupLabel}
          loading={lookup.status === "loading"}
          loadingLabel={strings.lookupLoading}
          message={lookup.status === "done" ? lookup.message : ""}
          onChange={setNct}
          onLookup={runLookup}
          placeholder={strings.lookupPlaceholder}
          value={nct}
        />
      }
      onChange={update}
      onClear={() => {
        setValues({});
        setLookup({ status: "idle" });
      }}
      onExample={() => setValues({ ...strings.example })}
      outputs={outputs}
      strings={strings}
      values={values}
    />
  );
}
